import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
} from 'react-router-dom';
import { lazy, Suspense } from 'react';
import Layout from '@/components/layout';
import LandingLayout from '@/pages/landing/components/LandingLayout';

// Lazy load all pages
const Login = lazy(() => import('@/pages/login'));
const RegisterMarket = lazy(() => import('@/pages/registration/market'));
const RegisterCoop = lazy(() => import('@/pages/registration/coop'));
const Welcome = lazy(() => import('@/pages/welcome'));
const Invitation = lazy(() => import('@/pages/invitation'));
const LandingPage = lazy(() => import('@/pages/landing'));
const NewsPage = lazy(() => import('@/pages/landing/News/NewsList'));
const NewsArticlePage = lazy(() => import('@/pages/landing/News/NewsDetail'));

// Admin Pages
const AdminPage = lazy(() => import('@/pages/admin'));
const AdminDashboard = lazy(() => import('@/pages/admin/dashboard'));
const AdminUsers = lazy(() => import('@/pages/admin/users'));
const AdminProducts = lazy(() => import('@/pages/admin/products'));
const AdminCooperatives = lazy(() => import('@/pages/admin/cooperatives'));
const AdminCooperativeList = lazy(
  () => import('@/pages/admin/cooperatives/cooperative-list'),
);
const AdminCooperativeDetails = lazy(
  () => import('@/pages/admin/cooperatives/cooperative-details'),
);
const AdminMarkets = lazy(() => import('@/pages/admin/markets'));
const AdminMarketList = lazy(() => import('@/pages/admin/markets/market-list'));
const AdminMarketDetails = lazy(
  () => import('@/pages/admin/markets/market-details'),
);
const AdminMarketReports = lazy(() => import('@/pages/admin/reports'));
const AdminMarketReportList = lazy(
  () => import('@/pages/admin/reports/ReportList'),
);
const AdminMarketReportDetails = lazy(
  () => import('@/pages/admin/reports/ReportDetails'),
);
const AdminNewsManagement = lazy(() => import('@/pages/admin/news'));
const AdminNewsEditor = lazy(() => import('@/pages/admin/news/NewsEditor'));
const AdminOrders = lazy(() => import('@/pages/admin/orders'));
const AdminOrderList = lazy(() => import('@/pages/admin/orders/order-list'));
const AdminOrderDetails = lazy(
  () => import('@/pages/admin/orders/order-details'),
);
const AdminBlockchain = lazy(() => import('@/pages/admin/blockchain'));

// Market pages
const MarketCooperatives = lazy(() => import('@/pages/market/cooperatives'));
const MarketCooperativeList = lazy(
  () => import('@/pages/market/cooperatives/cooperative-list'),
);
const MarketDashboard = lazy(() => import('@/pages/market/dashboard'));
const MarketCart = lazy(() => import('@/pages/market/cart'));
const MarketCartCheckout = lazy(
  () => import('@/pages/market/cart/cart-checkout'),
);
const MarketCartCompletion = lazy(
  () => import('@/pages/market/cart/cart-completion'),
);
const MarketProducts = lazy(() => import('@/pages/market/products'));
const MarketMap = lazy(() => import('@/pages/market/map'));
const MarketMissions = lazy(
  () => import('@/pages/market/mission-achievements'),
);
const MarketProductList = lazy(
  () => import('@/pages/market/products/product-list'),
);
const MarketProductDetails = lazy(
  () => import('@/pages/market/products/product-details'),
);
const MarketOrder = lazy(() => import('@/pages/market/orders'));
const MarketOrderList = lazy(() => import('@/pages/market/orders/order-list'));
const MarketOrderDetails = lazy(
  () => import('@/pages/market/orders/order-details'),
);
const MarketNotifications = lazy(
  () => import('@/pages/market/notifications/Notifications'),
);
const VouchersPage = lazy(
  () => import('@/pages/market/mission-achievements/VouchersPage'),
);
const MyVouchersPage = lazy(
  () => import('@/pages/market/mission-achievements/MyVouchersPage'),
);

// Cooperative pages
const CooperativeDashboard = lazy(
  () => import('@/pages/cooperative/dashboard'),
);
const CooperativeOrders = lazy(() => import('@/pages/cooperative/orders'));
const CooperativeOrdersList = lazy(
  () => import('@/pages/cooperative/orders/order-list'),
);
const CooperativeOrdersDetails = lazy(
  () => import('@/pages/cooperative/orders/order-details'),
);
const CooperativeProducts = lazy(() => import('@/pages/cooperative/products'));
const CooperativeProductDetails = lazy(
  () => import('@/pages/cooperative/products/product-details'),
);
const CoopProductList = lazy(
  () => import('@/pages/cooperative/products/product-list'),
);
const CoopProductForm = lazy(
  () => import('@/pages/cooperative/products/product-form'),
);
const EditProductForm = lazy(
  () => import('@/pages/cooperative/products/product-form/EditProductForm'),
);
const CooperativeMap = lazy(() => import('@/pages/cooperative/map'));
const CooperativeMissions = lazy(
  () => import('@/pages/cooperative/mission-achievements'),
);
const CooperativeNotifications = lazy(
  () => import('@/pages/cooperative/notifications/Notifications'),
);
const CooperativeVouchersPage = lazy(
  () => import('@/pages/cooperative/vouchers'),
);

// Generic Pages
const ChatPage = lazy(() => import('@/components/shared/chat'));
const Profile = lazy(() => import('@/components/shared/profile'));
const ShopPage = lazy(() => import('@/components/shared/shop'));
const FeatureComingSoon = lazy(
  () => import('@/components/shared/feature/FeatureComingSoon'),
);

// Loading component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-primary"></div>
  </div>
);

const AppRoutes: React.FC = () => {
  return (
    <Router>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route element={<LandingLayout />}>
            <Route path="/" element={<LandingPage />} />
            <Route path="/news" element={<NewsPage />} />
            <Route path="/news/:slug" element={<NewsArticlePage />} />
          </Route>

          <Route path="/login" element={<Login />} />
          <Route path="/register/market" element={<RegisterMarket />} />
          <Route path="/register/coop" element={<RegisterCoop />} />
          <Route path="/register/welcome" element={<Welcome />} />
          <Route path="/register/invitation" element={<Invitation />} />

          <Route path="/market" element={<Outlet />}>
            <Route element={<Layout />}>
              <Route path=":marketId" element={<MarketDashboard />} />
              <Route
                path=":marketId/cooperatives"
                element={<MarketCooperatives />}
              >
                <Route path="" element={<MarketCooperativeList />} />
                <Route path=":cooperativeId" element={<ShopPage />} />
              </Route>
              <Route path=":marketId/products" element={<MarketProducts />}>
                <Route path="" element={<MarketProductList />} />
                <Route path=":productId" element={<MarketProductDetails />} />
              </Route>
              <Route path=":marketId/cart" element={<MarketCart />}>
                <Route path="" element={<MarketCartCheckout />} />
                <Route path="completion" element={<MarketCartCompletion />} />
              </Route>
              <Route path=":marketId/map" element={<MarketMap />} />
              <Route path=":marketId/orders" element={<MarketOrder />}>
                <Route path="" element={<MarketOrderList />} />
                <Route path=":orderId" element={<MarketOrderDetails />} />
              </Route>
              <Route path=":marketId/missions" element={<MarketMissions />} />
              <Route
                path=":marketId/missions/vouchers"
                element={<VouchersPage />}
              />
              <Route
                path=":marketId/missions/my-vouchers"
                element={<MyVouchersPage />}
              />
              <Route path=":marketId/chats" element={<ChatPage />} />
              <Route path=":marketId/profile" element={<Profile />} />
              <Route
                path=":marketId/notifications"
                element={<MarketNotifications />}
              />
            </Route>
          </Route>

          <Route path="/cooperative" element={<Outlet />}>
            <Route element={<Layout />}>
              <Route path=":cooperativeId" element={<CooperativeDashboard />} />
              <Route
                path=":cooperativeId/orders"
                element={<CooperativeOrders />}
              >
                <Route path="" element={<CooperativeOrdersList />} />
                <Route path=":orderId" element={<CooperativeOrdersDetails />} />
              </Route>
              <Route
                path=":cooperativeId/products"
                element={<CooperativeProducts />}
              >
                <Route path="" element={<CoopProductList />} />
                <Route
                  path=":productId"
                  element={<CooperativeProductDetails />}
                />
                <Route path="create" element={<CoopProductForm />} />
                <Route path=":productId/edit" element={<EditProductForm />} />
              </Route>
              <Route
                path=":cooperativeId/vouchers"
                element={<CooperativeVouchersPage />}
              />
              <Route path=":cooperativeId/map" element={<CooperativeMap />} />
              <Route
                path=":cooperativeId/missions"
                element={<CooperativeMissions />}
              />
              <Route path=":cooperativeId/chats" element={<ChatPage />} />
              <Route path=":cooperativeId/profile" element={<Profile />} />
              <Route path=":cooperativeId/shop" element={<ShopPage />} />
              <Route
                path=":cooperativeId/notifications"
                element={<CooperativeNotifications />}
              />
            </Route>
          </Route>

          <Route path="/admin" element={<AdminPage />}>
            <Route element={<Layout />}>
              <Route path=":entityId" element={<AdminDashboard />} />
              <Route path=":entityId/users" element={<AdminUsers />} />
              <Route path=":entityId/products" element={<AdminProducts />} />
              <Route path=":entityId/orders" element={<AdminOrders />}>
                <Route path="" element={<AdminOrderList />} />
                <Route path=":orderId" element={<AdminOrderDetails />} />
              </Route>
              <Route
                path=":entityId/cooperatives"
                element={<AdminCooperatives />}
              >
                <Route path="" element={<AdminCooperativeList />} />
                <Route
                  path=":cooperativeId"
                  element={<AdminCooperativeDetails />}
                />
              </Route>
              <Route path=":entityId/markets" element={<AdminMarkets />}>
                <Route path="" element={<AdminMarketList />} />
                <Route path=":marketId" element={<AdminMarketDetails />} />
              </Route>
              <Route
                path=":entityId/products"
                element={<FeatureComingSoon />}
              />
              <Route
                path=":entityId/performance"
                element={<FeatureComingSoon />}
              />
              <Route path=":entityId/map" element={<FeatureComingSoon />} />
              <Route
                path=":entityId/mission-and-rewards"
                element={<FeatureComingSoon />}
              />
              <Route path=":entityId/news" element={<AdminNewsManagement />} />
              <Route
                path=":entityId/news/create"
                element={<AdminNewsEditor />}
              />
              <Route
                path=":entityId/news/edit/:id"
                element={<AdminNewsEditor />}
              />
              <Route path=":entityId/reports" element={<AdminMarketReports />}>
                <Route path="" element={<AdminMarketReportList />} />
                <Route
                  path=":reportId"
                  element={<AdminMarketReportDetails />}
                />
              </Route>
              <Route
                path=":entityId/blockchain"
                element={<AdminBlockchain />}
              />
            </Route>
          </Route>
        </Routes>
      </Suspense>
    </Router>
  );
};

export default AppRoutes;
